import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

// ── Types ──────────────────────────────────────────

export interface SevenFields {
  event_description: string;
  body_sensation: string;
  emotions: string[];
  emotion_free_input: string;
  meaning: string;
  automatic_response: string;
  new_response: string;
  result_gratitude: string;
}

export interface F1Event {
  id: number;
  user_id: string;
  sequence_no: number;
  state: string;
  event_description: string;
  body_sensation: string;
  emotions: string[];
  emotion_free_input: string | null;
  meaning: string;
  automatic_response: string;
  new_response: string;
  new_response_enacted: boolean;
  result_gratitude: string;
  confirmed_at: string | null;
  created_at: string;
}

export interface EntitlementData {
  free_uses_total: number;
  free_uses_consumed: number;
  free_uses_remaining: number;
  paid_tier: string | null;
  risk_paused: boolean;
  risk_reason: string | null;
}

export type PageState =
  | 'LOADING'
  | 'UNAUTHENTICATED'
  | 'CONSENT_REQUIRED'
  | 'F1_DRAFT'
  | 'SUBMITTING'
  | 'F1_SUBMITTED'
  | 'ENTITLEMENT_DENIED'
  | 'RISK_PAUSED'
  | 'ERROR'
  | 'SESSION_EXPIRED';

// ── Hook ───────────────────────────────────────────

export function usePracticeSupabase(user: { id: string } | null) {
  const [pageState, setPageState] = useState<PageState>('LOADING');
  const [draft, setDraft] = useState<SevenFields>(emptyDraft);
  const [submittedEvent, setSubmittedEvent] = useState<F1Event | null>(null);
  const [entitlement, setEntitlement] = useState<EntitlementData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<Record<string, unknown> | null>(null);

  // ── Load initial data when user is available ──
  const loadData = useCallback(async () => {
    if (!user) {
      setPageState('UNAUTHENTICATED');
      return;
    }

    setPageState('LOADING');
    setErrorMessage(null);

    try {
      // 1. Check consent
      const { data: consent, error: consentErr } = await supabase
        .from('practice_consent_records')
        .select('revoked_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (consentErr) {
        setPageState('ERROR');
        setErrorMessage('無法檢查同意書狀態：' + consentErr.message);
        return;
      }

      if (!consent) {
        setPageState('CONSENT_REQUIRED');
        return;
      }

      if (consent.revoked_at) {
        setPageState('CONSENT_REQUIRED');
        return;
      }

      // 2. Check if F1 already submitted
      const { data: existingF1, error: f1Err } = await supabase
        .from('practice_events')
        .select('*')
        .eq('user_id', user.id)
        .eq('sequence_no', 1)
        .eq('state', 'SUBMITTED')
        .maybeSingle();

      if (f1Err) {
        setPageState('ERROR');
        setErrorMessage('無法載入練習紀錄：' + f1Err.message);
        return;
      }

      if (existingF1) {
        setSubmittedEvent(existingF1 as F1Event);
        setPageState('F1_SUBMITTED');
        return;
      }

      // 3. Check for existing draft
      const { data: existingDraft, error: draftErr } = await supabase
        .from('practice_events')
        .select('*')
        .eq('user_id', user.id)
        .eq('sequence_no', 1)
        .eq('state', 'DRAFT')
        .maybeSingle();

      if (draftErr) {
        setPageState('ERROR');
        setErrorMessage('無法載入草稿：' + draftErr.message);
        return;
      }

      if (existingDraft) {
        setDraft({
          event_description: existingDraft.event_description || '',
          body_sensation: existingDraft.body_sensation || '',
          emotions: existingDraft.emotions || [],
          emotion_free_input: existingDraft.emotion_free_input || '',
          meaning: existingDraft.meaning || '',
          automatic_response: existingDraft.automatic_response || '',
          new_response: existingDraft.new_response || '',
          result_gratitude: existingDraft.result_gratitude || '',
        });
      }

      // 4. Load entitlement
      const { data: ent, error: entErr } = await supabase
        .from('user_entitlements')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (entErr) {
        setPageState('ERROR');
        setErrorMessage('無法載入權益資料：' + entErr.message);
        return;
      }

      const freeTotal = ent?.free_uses_total ?? 3;
      const freeConsumed = ent?.free_uses_consumed ?? 0;

      setEntitlement({
        free_uses_total: freeTotal,
        free_uses_consumed: freeConsumed,
        free_uses_remaining: freeTotal - freeConsumed,
        paid_tier: ent?.paid_tier ?? null,
        risk_paused: ent?.risk_paused ?? false,
        risk_reason: ent?.risk_reason ?? null,
      });

      if (ent?.risk_paused) {
        setPageState('RISK_PAUSED');
        return;
      }

      if (!ent?.paid_tier && freeConsumed >= freeTotal) {
        setPageState('ENTITLEMENT_DENIED');
        return;
      }

      setPageState('F1_DRAFT');
    } catch (err) {
      setPageState('ERROR');
      setErrorMessage(err instanceof Error ? err.message : '載入資料時發生未知錯誤');
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Save draft ──
  const saveDraft = useCallback(async (fields: SevenFields) => {
    if (!user) return;
    setDraft(fields);

    try {
      const { error } = await supabase
        .from('practice_events')
        .upsert({
          user_id: user.id,
          sequence_no: 1,
          state: 'DRAFT',
          event_description: fields.event_description,
          body_sensation: fields.body_sensation,
          emotions: fields.emotions,
          emotion_free_input: fields.emotion_free_input || null,
          meaning: fields.meaning,
          automatic_response: fields.automatic_response,
          new_response: fields.new_response,
          new_response_enacted: false,
          result_gratitude: fields.result_gratitude,
          draft: { last_saved: new Date().toISOString() },
        }, { onConflict: 'user_id,sequence_no' });

      if (error) {
        console.error('Failed to save draft:', error);
      }
    } catch (err) {
      console.error('Draft save error:', err);
    }
  }, [user]);

  // ── Give consent ──
  const giveConsent = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('practice_consent_records')
        .insert({
          user_id: user.id,
          consent_version: 'v1.0',
          consented_at: new Date().toISOString(),
          locale: 'zh-TW',
        });

      if (error) {
        setErrorMessage('無法儲存同意書：' + error.message);
        setPageState('ERROR');
        return;
      }

      // Reload full state (entitlement, etc.) after consent
      await loadData();
    } catch (err) {
      setPageState('ERROR');
      setErrorMessage(err instanceof Error ? err.message : '同意書儲存失敗');
    }
  }, [user, loadData]);

  // ── Submit F1 ──
  const submitF1 = useCallback(async (fields: SevenFields) => {
    if (!user) return;

    setPageState('SUBMITTING');
    setErrorMessage(null);

    const idempotencyKey = `f1-${user.id}-${Date.now()}`;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setPageState('SESSION_EXPIRED');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit_f1_practice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'X-Idempotency-Key': idempotencyKey,
          },
          body: JSON.stringify({
            idempotency_key: idempotencyKey,
            sequence_no: 1,
            event_description: fields.event_description,
            body_sensation: fields.body_sensation,
            emotions: fields.emotions,
            emotion_free_input: fields.emotion_free_input || null,
            meaning: fields.meaning,
            automatic_response: fields.automatic_response,
            new_response: fields.new_response,
            result_gratitude: fields.result_gratitude,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        const errCode = result?.error;

        if (errCode === 'NO_FREE_USES' || errCode === 'ENTITLEMENT_DENIED') {
          setPageState('ENTITLEMENT_DENIED');
          setEntitlement((prev) => prev ? {
            ...prev,
            free_uses_consumed: result.free_uses_consumed ?? prev.free_uses_consumed,
            free_uses_remaining: 0,
          } : null);
          return;
        }

        if (errCode === 'RISK_PAUSED') {
          setPageState('RISK_PAUSED');
          setEntitlement((prev) => prev ? {
            ...prev,
            risk_paused: true,
            risk_reason: result.risk_reason || null,
          } : null);
          return;
        }

        if (errCode === 'F1_EXISTS') {
          // Reload data
          await loadData();
          return;
        }

        setPageState('ERROR');
        setErrorMessage(result?.message || '提交失敗，請稍後再試');
        return;
      }

      // Success (result.success === true from atomic RPC)
      // result includes: { success, event, free_uses_consumed, free_uses_total, free_uses_remaining, idempotent }
      setSubmitResult(result);
      setSubmittedEvent(result.event as F1Event);
      setEntitlement((prev) => prev ? {
        ...prev,
        free_uses_consumed: result.free_uses_consumed as number,
        free_uses_remaining: result.free_uses_remaining as number,
      } : null);
      setPageState('F1_SUBMITTED');

    } catch (err) {
      setPageState('ERROR');
      setErrorMessage(err instanceof Error ? err.message : '網路連線失敗，請檢查後再試');
    }
  }, [user, loadData]);

  // ── Revoke consent ──
  const revokeConsent = useCallback(async () => {
    if (!user) return;
    try {
      await supabase
        .from('practice_consent_records')
        .update({ revoked_at: new Date().toISOString() })
        .eq('user_id', user.id);
      setPageState('CONSENT_REQUIRED');
      setSubmittedEvent(null);
    } catch (err) {
      console.error('Revoke consent error:', err);
    }
  }, [user]);

  // ── Reset (for consent re-grant) ──
  const resetToConsent = useCallback(() => {
    setPageState('CONSENT_REQUIRED');
    setErrorMessage(null);
  }, []);

  return {
    pageState,
    draft,
    submittedEvent,
    entitlement,
    errorMessage,
    submitResult,
    saveDraft,
    giveConsent,
    submitF1,
    revokeConsent,
    resetToConsent,
    loadData,
  };
}

const emptyDraft: SevenFields = {
  event_description: '',
  body_sensation: '',
  emotions: [],
  emotion_free_input: '',
  meaning: '',
  automatic_response: '',
  new_response: '',
  result_gratitude: '',
};